import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { IconButton } from 'copilot-design-system';
import React, { forwardRef, ReactNode, useState } from 'react';

const useStyles = makeStyles({
  dragging: {
    cursor: 'grabbing !important',
  },
});

interface SortableItemProps {
  id: UniqueIdentifier;
  children: (props: {
    attributes: any;
    listeners: any;
    setNodeRef: (node: HTMLElement | null) => void;
    isDragging: boolean;
    isActiveDragging: boolean;
  }) => ReactNode;
  isActiveDragging: boolean;
  isDragAndDropIconVisible: boolean;
}

const SortableItem = forwardRef<HTMLDivElement, SortableItemProps>(
  ({ id, children, isActiveDragging, isDragAndDropIconVisible }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
      id,
      animateLayoutChanges: () => true,
    });

    return (
      <div className={'group'} ref={setNodeRef}>
        <div className="relative">
          {isDragAndDropIconVisible && (
            <span {...listeners}>
              <IconButton
                className={clsx(
                  'p-0 absolute -left-6 cursor-grab top-1/3 group-hover:opacity-100 opacity-0 h-8 -translate-y-1/3 bg-transparent hover:bg-gray-100 py-2 px-0.5 active:bg-gray-300',
                  {
                    'pointer-events-none': isActiveDragging,
                  },
                )}
                icon="DragDrop"
                variant="minimal"
              />
            </span>
          )}

          {children({
            attributes,
            listeners,
            setNodeRef,
            isDragging,
            isActiveDragging,
          })}
        </div>
      </div>
    );
  },
);

interface Props<T> {
  items: T[];
  onReorder: (newItems: T[], oldItems: T[]) => void;
  renderItem: (
    item: T,
    index: number,
    isDragging: boolean,
    isActiveDragging: boolean,
  ) => ReactNode;
  renderOverlay?: (item: T, index: number) => ReactNode;
  draggableKey?: string;
  overLayBorder?: boolean;
  shouldBeDragItem: boolean;
}

const useDndSensors = () => {
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  return useSensors(pointerSensor, keyboardSensor);
};

export function DndWrapper<T>({
  items,
  onReorder,
  renderItem,
  renderOverlay,
  draggableKey,
  overLayBorder,
  shouldBeDragItem,
}: Props<T>) {
  const classes = useStyles();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const activeIndex = items.findIndex((_, i) => `${i}` === activeId);
  const sensors = useDndSensors();

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    document.body.classList.add(classes.dragging);
  };

  const handleDragEnd = ({ over }: DragEndEvent) => {
    setActiveId(null);
    setDropIndex(null);
    document.body.classList.remove(classes.dragging);

    if (over) {
      const overIndex = items.findIndex((_, i) => `${i}` === over.id);
      if (activeIndex !== overIndex) {
        const newItems = arrayMove(items, activeIndex, overIndex);
        onReorder(newItems, items);
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setDropIndex(null);
    document.body.classList.remove(classes.dragging);
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    if (over) {
      const overIndex = items.findIndex((_, i) => `${i}` === over.id);
      const newDropIndex = overIndex >= activeIndex ? overIndex + 1 : overIndex;
      setDropIndex(newDropIndex);
    } else {
      setDropIndex(items.length);
    }
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        onDragOver={handleDragOver}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      >
        <SortableContext
          items={items.map((_, index) => index.toString())}
          strategy={verticalListSortingStrategy}
        >
          <div className="relative">
            {activeId !== null && dropIndex !== null && (
              <div
                className="absolute left-0 right-0 h-[1px] bg-[#053299] transition-all duration-200 ease-in-out pointer-events-none"
                style={{
                  top: `${
                    dropIndex === items.length
                      ? 100
                      : (dropIndex * 100) / items.length
                  }%`,
                  zIndex: 10,
                }}
              />
            )}
            {items.map((item, index) => (
              <React.Fragment key={`${draggableKey}-${index}`}>
                <SortableItem
                  id={index.toString()}
                  isActiveDragging={activeId !== null}
                  isDragAndDropIconVisible={shouldBeDragItem}
                >
                  {({ isDragging, isActiveDragging }) =>
                    renderItem(item, index, isDragging, isActiveDragging)
                  }
                </SortableItem>
              </React.Fragment>
            ))}
          </div>
        </SortableContext>
        <DragOverlay
          dropAnimation={null}
          className={clsx({
            'border-solid border-dark-black bg-white': overLayBorder,
          })}
        >
          {activeId !== null &&
            renderOverlay &&
            renderOverlay(items[Number(activeId)], Number(activeId))}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
